import { z } from 'zod'
import { formatDayRange } from './day-labels.ts'
import { PLAN_TIME_KINDS, shiftDate, sortPlanTimes } from './plan-times.ts'

/**
 * Plan templates (docs/14-TEAMS-PEOPLE-TEMPLATES.md): a service type's usual
 * plan — its running order, its times, the people each position needs, its
 * notes — that new plans start from, one at a time or several at once.
 *
 * A template has no date. Its times are a day relative to the plan's
 * (0: the day chosen for the plan; -3: the Thursday before a Sunday) and a
 * wall time, so the same template makes a plan on any day.
 */

/** How far from the plan's day a template's time may be: four weeks either way. */
export const TEMPLATE_DAY_RANGE = 28

/** The most plans made at once: a year of weekly Sundays. */
export const MAX_PLANS_AT_ONCE = 52

/** The longest gap between plans made at once, in weeks. */
export const MAX_REPEAT_WEEKS = 12

export interface TemplateTimeLike {
  id: string
  kind: string
  name: string | null
  dayOffset: number
  startTime: string
  endTime: string | null
}

const clockPattern = /^[0-2][0-9]:[0-5][0-9]$/

export const templateTimeSchema = z
  .object({
    kind: z.enum(PLAN_TIME_KINDS),
    name: z.string().trim().max(60).nullish(),
    dayOffset: z
      .number()
      .int()
      .min(-TEMPLATE_DAY_RANGE, 'A time can be at most four weeks before the plan’s day.')
      .max(TEMPLATE_DAY_RANGE, 'A time can be at most four weeks after the plan’s day.'),
    startTime: z.string().regex(clockPattern),
    endTime: z.string().regex(clockPattern).nullish(),
  })
  .refine((t) => !t.endTime || t.endTime > t.startTime, {
    message: 'It ends before it starts.',
    path: ['endTime'],
  })

/** "Plan day", "3 days before", "1 day after": where a template's time falls. */
export function dayOffsetLabel(offset: number): string {
  if (offset === 0) return 'Plan day'
  const days = Math.abs(offset)
  return `${days} ${days === 1 ? 'day' : 'days'} ${offset < 0 ? 'before' : 'after'}`
}

/** The day choices a template's time offers, nearest the plan's day in the middle. */
export function dayOffsetChoices(range = 7): { value: number; label: string }[] {
  return Array.from({ length: range * 2 + 1 }, (_, i) => i - range).map((value) => ({
    value,
    label: dayOffsetLabel(value),
  }))
}

/**
 * A template's times as one plan's: each on its day relative to `planDate`,
 * in the order they happen.
 */
export function templateTimesOn<T extends TemplateTimeLike>(
  planDate: string,
  times: readonly T[],
): (Omit<T, 'dayOffset'> & { date: string })[] {
  return sortPlanTimes(
    times.map(({ dayOffset, ...time }) => ({ ...time, date: shiftDate(planDate, dayOffset) })),
  )
}

/** A template's times in the order they happen (by day, then start, services first). */
export function sortTemplateTimes<T extends TemplateTimeLike>(times: readonly T[]): T[] {
  const anchor = '2000-01-02'
  const byId = new Map(times.map((time) => [time.id, time]))
  return templateTimesOn(anchor, times).flatMap((time) => byId.get(time.id) ?? [])
}

/** The template's first service: where its running order's clock starts. */
export function firstTemplateService<T extends TemplateTimeLike>(
  times: readonly T[],
): T | undefined {
  return sortTemplateTimes(times.filter((time) => time.kind === 'service'))[0]
}

/** How many plans New plan makes, and how often. */
export interface PlanRepeat {
  /** Weekly is 1; every other week 2. */
  everyWeeks: number
  /** The last day a plan may be on (inclusive). */
  until: string
}

export const planRepeatSchema = z.object({
  everyWeeks: z.number().int().min(1).max(MAX_REPEAT_WEEKS),
  until: z.iso.date(),
})

/**
 * The days New plan makes plans on: `first`, then every `everyWeeks` weeks
 * up to `until` (inclusive). Never more than `max` + 1 days — enough to tell
 * that a choice makes too many without listing a century of Sundays.
 */
export function repeatDates(
  first: string,
  repeat: PlanRepeat | null,
  max: number = MAX_PLANS_AT_ONCE,
): string[] {
  if (!repeat) return [first]
  const step = Math.max(1, Math.trunc(repeat.everyWeeks)) * 7
  const out: string[] = []
  for (let day = first; day <= repeat.until && out.length <= max; day = shiftDate(day, step))
    out.push(day)
  return out
}

/** Why a set of days can't be made: none, or too many at once. Null when they can. */
export function repeatProblem(dates: readonly string[], max = MAX_PLANS_AT_ONCE): string | null {
  if (dates.length === 0) return 'The last day is before the first.'
  if (dates.length > max) return `That makes more than ${max} plans: at most ${max} at once.`
  return null
}

/** "Makes 6 plans: Nov 1 – Dec 6", or "Makes 1 plan: Nov 1". */
export function repeatSummary(dates: readonly string[]): string {
  const first = dates[0]
  const last = dates.at(-1)
  if (!first || !last) return 'Makes no plans'
  const range =
    dates.length === 1 ? formatDayRange(first, first).split(' – ')[0] : formatDayRange(first, last)
  return `Makes ${dates.length} ${dates.length === 1 ? 'plan' : 'plans'}: ${range}`
}

/**
 * The order New plan offers a service type's templates in: its default
 * first, then the church's order, then by name. "Blank" comes after them.
 */
export function templatePickerOrder<
  T extends { id: string; name: string; isDefault: boolean; sortOrder: number },
>(templates: readonly T[]): T[] {
  return [...templates].sort(
    (a, b) =>
      Number(b.isDefault) - Number(a.isDefault) ||
      a.sortOrder - b.sortOrder ||
      a.name.localeCompare(b.name) ||
      a.id.localeCompare(b.id),
  )
}
